import { redactSensitive } from './redaction';

const MAX_LINKS = 5;
const MAX_DETAIL_CHARS = 2000;
const SCALAR_FIELDS = ['name', 'message', 'kind', 'code', 'status', 'statusCode', 'hint'] as const;

type CauseLink = Record<string, boolean | number | string>;

/**
 * Every level of `error.cause`, plain error objects included. Sentry links
 * only `Error` causes, so an application or provider error wrapped in an
 * `Error` arrived as a bare message; this keeps what actually failed (kind,
 * provider code, details). Links are flat: each detail is its own redacted
 * `detail_<key>` field, so Sentry's context depth limit cannot collapse them
 * into `[Object]` and its scrubber filters one field rather than all of them.
 */
export function describeCauseChain(error: unknown): Record<string, CauseLink> {
  const chain: Record<string, CauseLink> = {};
  const seen = new Set<object>();
  let current: unknown = error;
  for (let index = 0; index < MAX_LINKS; index += 1) {
    if (typeof current !== 'object' || current === null || seen.has(current)) break;
    seen.add(current);
    const source = current as Record<string, unknown>;
    chain[String(index)] = describeLink(source);
    current = source.cause;
  }
  return chain;
}

function describeLink(source: Record<string, unknown>): CauseLink {
  const link: CauseLink = {};
  for (const field of SCALAR_FIELDS) {
    const value = source[field];
    if (isScalar(value)) link[field] = value;
  }
  return { ...link, ...describeDetails(redactSensitive(source.details)) };
}

function describeDetails(details: unknown): CauseLink {
  if (details === undefined || details === null) return {};
  if (typeof details === 'string') return { details: truncate(details) };
  if (typeof details !== 'object' || Array.isArray(details)) {
    return { details: truncate(JSON.stringify(details) ?? '') };
  }
  const fields: CauseLink = {};
  for (const [key, value] of Object.entries(details)) {
    if (value === undefined || value === null) continue;
    fields[`detail_${key}`] = isScalar(value) ? value : truncate(JSON.stringify(value) ?? '');
  }
  return fields;
}

function isScalar(value: unknown): value is boolean | number | string {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function truncate(text: string): string {
  return text.length > MAX_DETAIL_CHARS ? `${text.slice(0, MAX_DETAIL_CHARS)}…` : text;
}
